import { emit } from '../socket';

const EventsService = {
  getAllEvents: async () => {
    const r = await emit('event:list');
    return r.data;
  },
  getEvent: async (eventId) => {
    const r = await emit('event:get', { eventId });
    return r.data;
  },
  createEvent: async (name) => {
    const r = await emit('event:create', { name });
    return r.data;
  },
  deleteEvent: async (eventId) => {
    await emit('event:delete', { eventId });
  },
  duplicateEvent: async (eventId) => {
    await emit('event:duplicate', { eventId });
  },
  startEvent: async (eventId) => {
    await emit('event:start', { eventId });
  },
  endEvent: async (eventId) => {
    await emit('event:end', { eventId });
  },
  resumeEvent: async (eventId) => {
    await emit('event:resume', { eventId });
  },
  undoTimepoint: async (eventId) => {
    await emit('event:undo', { eventId });
  },
  deleteTimepoint: async (timepointId) => {
    await emit('event:deleteTimepoint', { timepointId });
  },
  editTimepoint: async (timepointId, data) => {
    const r = await emit('event:editTimepoint', { timepointId, ...data });
    return r.data;
  },
  renameEvent: async (eventId, name) => {
    const r = await emit('event:rename', { eventId, name });
    return r.data;
  },
  updateEventActions: async (eventId, actions) => {
    await emit('event:updateActions', { eventId, actions });
  },
  addTimepoint: async (eventId, type, time, endTime) => {
    const payload = { eventId, type, time };
    if (endTime !== undefined) payload.endTime = endTime;
    const r = await emit('event:addTimepoint', payload);
    return r.data;
  },
  getTimeArray: async (eventId) => {
    const r = await emit('event:getTimeArray', { eventId });
    return r.data;
  },
  exportEvent: async (eventId) => {
    const r = await emit('event:export', { eventId });
    return r.data;
  },

  // Alarms
  createAlarm: async (data) => {
    const r = await emit('alarm:create', data);
    return r.data;
  },
  updateAlarm: async (alarmId, data) => {
    const r = await emit('alarm:update', { alarmId, ...data });
    return r.data;
  },
  deleteAlarm: async (alarmId) => {
    await emit('alarm:delete', { alarmId });
  },
  listAlarms: async (eventId) => {
    const r = await emit('alarm:list', { eventId });
    return r.data;
  },
  listGlobalAlarms: async () => {
    const r = await emit('alarm:listGlobal');
    return r.data;
  },

  // Parallel events
  listActiveEvents: async () => {
    const r = await emit('event:listActive');
    return r.data;
  },

  // Persistent actions
  createPersistentAction: async (data) => {
    const r = await emit('persistent:create', data);
    return r.data;
  },
  listPersistentActions: async () => {
    const r = await emit('persistent:list');
    return r.data;
  },
  deletePersistentAction: async (actionId) => {
    await emit('persistent:delete', { actionId });
  },
  logPersistentAction: async (persistentActionId) => {
    const r = await emit('persistent:log', { persistentActionId });
    return r.data;
  },
  getPersistentLogs: async (persistentActionId) => {
    const r = await emit('persistent:logs', { persistentActionId });
    return r.data;
  },

  // Timeline
  getTimelineDay: async (date) => {
    const tzOffset = new Date().getTimezoneOffset();
    const r = await emit('timeline:day', { date, tzOffset });
    return r.data;
  },
  getTimelineWeek: async (weekStart) => {
    const tzOffset = new Date().getTimezoneOffset();
    const r = await emit('timeline:week', { weekStart, tzOffset });
    return r.data;
  },
  getTimelineMonth: async (year, month) => {
    const r = await emit('timeline:month', { year, month });
    return r.data;
  },
  getTimelineYear: async (year) => {
    const r = await emit('timeline:year', { year });
    return r.data;
  },

  // Analytics
  getEventChart: async (eventId) => {
    const r = await emit('analytics:eventChart', { eventId });
    return r.data;
  },
  getActionFrequency: async (eventId) => {
    const r = await emit('analytics:actionFrequency', { eventId });
    return r.data;
  },
  getIntervalDistribution: async (eventId, typeId) => {
    const r = await emit('analytics:intervalDistribution', { eventId, typeId });
    return r.data;
  },
  getCrossEventAnalytics: async (data) => {
    const r = await emit('analytics:crossEvent', data);
    return r.data;
  },
  getCorrelations: async (data) => {
    const r = await emit('analytics:correlations', data);
    return r.data;
  },
  getTimeBudget: async (dateFrom, dateTo) => {
    const r = await emit('analytics:timeBudget', { dateFrom, dateTo });
    return r.data;
  },

  // Day Rating
  setDayRating: async (data) => {
    const r = await emit('dayRating:set', data);
    return r.data;
  },
  getDayRating: async (date) => {
    const r = await emit('dayRating:get', { date });
    return r.data;
  },
  getDayRatingRange: async (from, to) => {
    const r = await emit('dayRating:range', { from, to });
    return r.data;
  },

  // Daily Digest
  getDailyDigest: async (date) => {
    const r = await emit('digest:daily', { date });
    return r.data;
  },

  // Templates
  createTemplate: async (data) => {
    const r = await emit('template:create', data);
    return r.data;
  },
  listTemplates: async () => {
    const r = await emit('template:list');
    return r.data;
  },
  getTemplate: async (templateId) => {
    const r = await emit('template:get', { templateId });
    return r.data;
  },
  deleteTemplate: async (templateId) => {
    await emit('template:delete', { templateId });
  },
  quickStartFromTemplate: async (templateId) => {
    const r = await emit('template:quickStart', { templateId });
    return r.data;
  },

  // Achievements
  listAchievements: async () => {
    const r = await emit('achievement:list');
    return r.data;
  },
  getLatestAchievements: async () => {
    const r = await emit('achievement:latest');
    return r.data;
  },

  // Time Budget Goals
  setTimeBudgetGoal: async (data) => {
    const r = await emit('timeBudget:setGoal', data);
    return r.data;
  },
  listTimeBudgetGoals: async () => {
    const r = await emit('timeBudget:listGoals');
    return r.data;
  },

  // Search
  searchEvents: async (data) => {
    const r = await emit('event:search', data);
    return r.data;
  },

  // Webhooks
  createWebhook: async (data) => {
    const r = await emit('webhook:create', data);
    return r.data;
  },
  listWebhooks: async () => {
    const r = await emit('webhook:list');
    return r.data;
  },
  deleteWebhook: async (webhookId) => {
    await emit('webhook:delete', { webhookId });
  },

  // Shared Events (public multi-player events)
  createSharedEvent: async (data) => {
    const r = await emit('sharedEvent:create', data);
    return r.data;
  },
  joinSharedEvent: async (code, displayName) => {
    const r = await emit('sharedEvent:join', { code, displayName });
    return r.data;
  },
  getSharedEvent: async (sharedEventId) => {
    const r = await emit('sharedEvent:get', { sharedEventId });
    return r.data;
  },
  listSharedEvents: async () => {
    const r = await emit('sharedEvent:list');
    return r.data;
  },
  startSharedEvent: async (sharedEventId) => {
    const r = await emit('sharedEvent:start', { sharedEventId });
    return r.data;
  },
  endSharedEvent: async (sharedEventId) => {
    const r = await emit('sharedEvent:end', { sharedEventId });
    return r.data;
  },
  addSharedTimepoint: async (sharedEventId, actionId) => {
    const r = await emit('sharedEvent:addTimepoint', { sharedEventId, actionId });
    return r.data;
  },
  getSharedTimeArray: async (sharedEventId) => {
    const r = await emit('sharedEvent:getTimeArray', { sharedEventId });
    return r.data;
  },
  leaveSharedEvent: async (sharedEventId) => {
    await emit('sharedEvent:leave', { sharedEventId });
  },
};

export default EventsService;
